'use client';

import React from 'react';
import { X } from 'lucide-react';
import { BacktestRun } from '../utils/backtestStorage';

interface BacktestComparePanelProps {
  runs: BacktestRun[];
  onClose: () => void;
}

const metrics = [
  { key: 'winRate', label: 'Win Rate', format: (v: number) => `${v}%`, higherBetter: true },
  { key: 'profitFactor', label: 'Profit Factor', format: (v: number) => v.toFixed(2), higherBetter: true },
  { key: 'netReturn', label: 'Net Return ($)', format: (v: number) => `${v >= 0 ? '+' : ''}$${v.toLocaleString()}`, higherBetter: true },
  { key: 'netReturnPct', label: 'Net Return (%)', format: (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`, higherBetter: true },
  { key: 'maxDrawdownPct', label: 'Max Drawdown', format: (v: number) => `-${v.toFixed(1)}%`, higherBetter: false },
  { key: 'totalTrades', label: 'Total Trades', format: (v: number) => v.toString(), higherBetter: null },
  { key: 'wins', label: 'Wins', format: (v: number) => v.toString(), higherBetter: true },
  { key: 'losses', label: 'Losses', format: (v: number) => v.toString(), higherBetter: false },
  { key: 'grossProfit', label: 'Gross Profit', format: (v: number) => `$${v.toLocaleString()}`, higherBetter: true },
  { key: 'grossLoss', label: 'Gross Loss', format: (v: number) => `$${v.toLocaleString()}`, higherBetter: false },
];

export default function BacktestComparePanel({ runs, onClose }: BacktestComparePanelProps) {
  const getBest = (key: string, higherBetter: boolean | null) => {
    if (higherBetter === null) return null;
    const values = runs.map((r) => (r.results as Record<string, number>)[key]);
    return higherBetter ? Math.max(...values) : Math.min(...values);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="bg-card border border-border rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 className="text-base font-bold text-foreground">Strategy Comparison</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Side-by-side analysis of {runs.length} backtest runs</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
            <X size={16} />
          </button>
        </div>

        <div className="overflow-auto flex-1">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-card z-10">
              <tr className="border-b border-border">
                <th className="px-5 py-3 text-left text-xs font-500 text-muted-foreground uppercase tracking-wider w-40">Metric</th>
                {runs.map((run) => (
                  <th key={`cmp-hdr-${run.id}`} className="px-5 py-3 text-left min-w-[180px]">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${run.results.netReturn >= 0 ? 'bg-positive' : 'bg-negative'}`} />
                      <div>
                        <p className="text-xs font-600 text-foreground truncate max-w-[140px]">{run.name}</p>
                        <p className="text-xs text-muted-foreground font-mono-nums">{run.params.symbol} · {run.params.timeframe}</p>
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
              {/* Params row */}
              <tr className="border-b border-border bg-muted/20">
                <td className="px-5 py-2 text-xs text-muted-foreground font-500">Period</td>
                {runs.map((run) => (
                  <td key={`cmp-period-${run.id}`} className="px-5 py-2 text-xs text-foreground font-mono-nums">
                    {run.params.startDate} → {run.params.endDate}
                  </td>
                ))}
              </tr>
              <tr className="border-b border-border bg-muted/20">
                <td className="px-5 py-2 text-xs text-muted-foreground font-500">Capital</td>
                {runs.map((run) => (
                  <td key={`cmp-cap-${run.id}`} className="px-5 py-2 text-xs text-foreground font-mono-nums">
                    ${parseInt(run.params.initialCapital).toLocaleString()}
                  </td>
                ))}
              </tr>
              <tr className="border-b border-border bg-muted/20">
                <td className="px-5 py-2 text-xs text-muted-foreground font-500">Risk/Trade</td>
                {runs.map((run) => (
                  <td key={`cmp-risk-${run.id}`} className="px-5 py-2 text-xs text-foreground font-mono-nums">
                    {run.params.riskPerTrade}%
                  </td>
                ))}
              </tr>
            </thead>
            <tbody>
              {metrics.map((metric, idx) => {
                const best = getBest(metric.key, metric.higherBetter);
                return (
                  <tr key={`cmp-row-${metric.key}`} className={`border-b border-border/40 ${idx % 2 === 0 ? 'bg-transparent' : 'bg-muted/10'}`}>
                    <td className="px-5 py-3 text-xs font-500 text-muted-foreground">{metric.label}</td>
                    {runs.map((run) => {
                      const val = (run.results as Record<string, number>)[metric.key];
                      const isBest = best !== null && val === best;
                      return (
                        <td key={`cmp-cell-${run.id}-${metric.key}`} className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-600 font-mono-nums ${
                              isBest
                                ? metric.higherBetter
                                  ? 'text-positive' :'text-warning' :'text-foreground'
                            }`}>
                              {metric.format(val)}
                            </span>
                            {isBest && metric.higherBetter !== null && (
                              <span className={`text-xs px-1.5 py-0.5 rounded-full font-500 ${
                                metric.higherBetter
                                  ? 'bg-positive-subtle text-positive' :'bg-warning/10 text-warning'
                              }`}>
                                {metric.higherBetter ? 'Best' : 'Lowest'}
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mini equity curves */}
        <div className="border-t border-border px-6 py-4">
          <p className="text-xs font-500 text-muted-foreground uppercase tracking-wider mb-3">Equity Curves</p>
          <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${runs.length}, 1fr)` }}>
            {runs.map((run) => {
              const isProfit = run.results.netReturn >= 0;
              const values = run.results.equityCurve.map((d) => d.equity);
              const min = Math.min(...values);
              const max = Math.max(...values);
              const range = max - min || 1;
              const w = 100; const h = 48;
              const pts = values.map((v, i) => {
                const x = (i / (values.length - 1)) * w;
                const y = h - ((v - min) / range) * h;
                return `${x},${y}`;
              });
              return (
                <div key={`cmp-curve-${run.id}`} className="bg-muted/30 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-2 truncate">{run.name}</p>
                  <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-12" preserveAspectRatio="none">
                    <polyline points={pts.join(' ')} fill="none" stroke={isProfit ? 'var(--positive)' : 'var(--negative)'} strokeWidth="1.5" />
                  </svg>
                  <p className={`text-xs font-600 font-mono-nums mt-1 ${isProfit ? 'text-positive' : 'text-negative'}`}>
                    {isProfit ? '+' : ''}${run.results.netReturn.toLocaleString()} ({isProfit ? '+' : ''}{run.results.netReturnPct.toFixed(1)}%)
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
